/**
 * ReportContent - 共用報告排版元件
 * 將 R 後端輸出的文字報告解析並渲染為美化的 Material UI 元件
 *
 * 支援格式：
 *   # 標題 (深紫色粗體大字)
 *   ## 副標題 (中紫色粗體)
 *   ### 紅色標題 (深紅色粗體)
 *   #### 紅色副標題 (中紅色粗體)
 *   【】 標題 (紫色漸層)
 *   | Markdown 表格
 *   ASCII 圖表 (label | #### score)
 *   建議/注意/重要/提醒 高亮提示
 *   * / - / . 列表項
 */

import React from 'react';
import {
  Box,
  Typography,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine
} from 'recharts';

// ================================================================
// Emoji 對照表
// ================================================================

const bracketToEmoji: Record<string, string> = {
  // 基本標記
  '[位置]': '\u{1F4CD} ',
  '[評語]': '\u{1F4AC} ',
  '[統計]': '\u{1F4CA} ',
  '[提示]': '\u{1F4A1} ',
  '[重點]': '\u{1F3AF} ',
  '[趨勢]': '\u{1F4C8} ',
  '[日期]': '\u{1F4C5} ',
  '[時間]': '\u{23F0} ',
  '[年齡]': '\u{1F382} ',
  '[流年]': '\u{1F52E} ',
  '[農曆]': '\u{1F319} ',
  '[人物]': '\u{1F464} ',
  '[警告]': '\u26A0\uFE0F ',
  '[建議]': '\u2728 ',

  // 感情相關
  '[愛]': '\u{1F496} ',
  '[戀愛]': '\u{1F495} ',
  '[錢]': '\u{1F4B0} ',
  '[財]': '\u{1F4B8} ',
  '[婚姻]': '\u{1F48D} ',
  '[家庭]': '\u{1F3E0} ',
  '[父母]': '\u{1F468}\u200D\u{1F469}\u200D\u{1F466} ',
  '[子女]': '\u{1F476} ',
  '[朋友]': '\u{1F46B} ',
  '[夥伴]': '\u{1F91D} ',

  // 事業財富
  '[金錢]': '\u{1F4B0} ',
  '[財富]': '\u{1F48E} ',
  '[事業]': '\u{1F4BC} ',
  '[工作]': '\u{1F454} ',
  '[升遷]': '\u{1F4C8} ',
  '[貴人]': '\u{1F31F} ',
  '[機會]': '\u{1F3AF} ',

  // 健康與生活
  '[健康]': '\u{1F4AA} ',
  '[疾病]': '\u{1F3E5} ',
  '[休息]': '\u{1F634} ',
  '[飲食]': '\u{1F37D}\uFE0F ',
  '[運動]': '\u{1F3C3} ',

  // 學習與智慧
  '[學習]': '\u{1F4DA} ',
  '[智慧]': '\u{1F9E0} ',
  '[考試]': '\u{1F4DD} ',
  '[創意]': '\u{1F4A1} ',

  // 特殊符號
  '[寶石]': '\u{1F48E} ',
  '[閃電]': '\u26A1 ',
  '[星]': '\u{1F31F} ',
  '[閃]': '\u2728 ',
  '[獎杯]': '\u{1F3C6} ',
  '[火]': '\u{1F525} ',
  '[水]': '\u{1F4A7} ',
  '[木]': '\u{1F333} ',
  '[金]': '\u269C\uFE0F ',
  '[土]': '\u{1F3D4}\uFE0F ',
};

const replaceBracketMarkers = (text: string): string => {
  let result = text;
  for (const [bracket, emoji] of Object.entries(bracketToEmoji)) {
    result = result.replaceAll(bracket, emoji);
  }
  return result;
};

// ================================================================
// 圖表解析
// ================================================================

const chartLinePattern = /^(.+?)\s*\|\s*[#]+\s*([\d.-]+)/;
const chartLinePatternBracket = /^.*?([\d.-]+)\s*\[([#-]+)\]\s*$/;

interface ChartDataPoint {
  label: string;
  value: number;
}

const extractChartData = (lines: string[]): ChartDataPoint[] => {
  const data: ChartDataPoint[] = [];
  for (const line of lines) {
    const match = line.trim().match(chartLinePattern);
    if (match) {
      data.push({
        label: match[1].trim().replace(/\s*\(.*?\)\s*$/, ''),
        value: parseFloat(match[2])
      });
    } else {
      const match2 = line.trim().match(/^(.+?)\s+([\d.-]+)\s*\[([#-]+)\]$/);
      if (match2) {
        data.push({
          label: match2[1].trim().replace(/^[★\s]+/, '').replace(/:\s*$/, ''),
          value: parseFloat(match2[2])
        });
      }
    }
  }
  return data;
};

// ================================================================
// Section 型別
// ================================================================

type SectionType = 'title' | 'subtitle' | 'red-title' | 'red-subtitle' | 'highlight' | 'paragraph' | 'list' | 'divider' | 'empty' | 'chart' | 'table';

interface TableData {
  headers: string[];
  rows: string[][];
  alignments?: ('left' | 'center' | 'right')[];
}

interface Section {
  type: SectionType;
  content: string;
  chartData?: ChartDataPoint[];
  chartTitle?: string;
  tableData?: TableData;
}

// ================================================================
// 解析函數
// ================================================================

const parseReportContent = (text: string): Section[] => {
  if (!text) return [];

  const debugPatterns = /^\s*\[(?:查詢|OK|INFO|WARN|ERROR|DEBUG|新增|確認|處理|載入|連接|開始|完成|結束)\]/;
  const allLines = text.split('\n').filter(line => !debugPatterns.test(line));
  const sections: Section[] = [];

  let i = 0;
  while (i < allLines.length) {
    const trimmed = allLines[i].trim();

    // 空行
    if (!trimmed) {
      sections.push({ type: 'empty', content: '' });
      i++;
      continue;
    }

    // 分隔線
    if (/^[=\-*\s]{3,}$/.test(trimmed) && /[=\-*]/.test(trimmed)) {
      sections.push({ type: 'divider', content: '' });
      i++;
      continue;
    }

    // Markdown 表格
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      const tableLines: string[] = [trimmed];
      let j = i + 1;
      while (j < allLines.length) {
        const line = allLines[j].trim();
        if (line.startsWith('|') && line.endsWith('|')) {
          tableLines.push(line);
          j++;
        } else if (!line) {
          j++;
          break;
        } else {
          break;
        }
      }

      if (tableLines.length >= 2) {
        const headers = tableLines[0].split('|').map(h => h.trim()).filter(h => h);
        const alignmentLine = tableLines[1];
        const alignments = alignmentLine.split('|').map(a => a.trim()).filter(a => a).map(a => {
          if (a.startsWith(':') && a.endsWith(':')) return 'center' as const;
          if (a.endsWith(':')) return 'right' as const;
          return 'left' as const;
        });
        const rows = tableLines.slice(2)
          .map(line => line.split('|').map(c => c.trim()).filter(c => c))
          .filter(row => row.length > 0);

        if (headers.length > 0 && rows.length > 0) {
          sections.push({ type: 'table', content: '', tableData: { headers, rows, alignments } });
          i = j;
          continue;
        }
      }
      sections.push({ type: 'paragraph', content: replaceBracketMarkers(trimmed) });
      i++;
      continue;
    }

    // ASCII 圖表
    if (chartLinePattern.test(trimmed) || chartLinePatternBracket.test(trimmed)) {
      const chartLines: string[] = [];
      let chartTitle = '';
      for (let j = sections.length - 1; j >= 0; j--) {
        if (sections[j].type === 'empty' || sections[j].type === 'divider') continue;
        if (sections[j].content && (
          sections[j].content.includes('\u{1F4C8}') || // 趨勢 emoji
          sections[j].content.includes('趨勢') ||
          sections[j].content.includes('運勢') ||
          sections[j].content.includes('起伏') ||
          sections[j].content.includes('圖')
        )) {
          chartTitle = sections[j].content;
        }
        break;
      }
      while (i < allLines.length) {
        const line = allLines[i].trim();
        if (!line) { i++; break; }
        if (chartLinePattern.test(line) || /\[#/.test(line)) {
          chartLines.push(line);
          i++;
        } else {
          break;
        }
      }
      const chartData = extractChartData(chartLines);
      if (chartData.length >= 2) {
        sections.push({ type: 'chart', content: '', chartData, chartTitle });
      } else {
        for (const cl of chartLines) {
          sections.push({ type: 'paragraph', content: replaceBracketMarkers(cl) });
        }
      }
      continue;
    }

    const beautified = replaceBracketMarkers(trimmed);

    // #### 紅色副標題
    if (beautified.startsWith('####')) {
      sections.push({ type: 'red-subtitle', content: beautified.replace(/^####\s*/, '') });
      i++;
      continue;
    }

    // ### 紅色標題
    if (beautified.startsWith('###') && !beautified.startsWith('####')) {
      sections.push({ type: 'red-title', content: beautified.replace(/^###\s*/, '') });
      i++;
      continue;
    }

    // ## 副標題
    if (beautified.startsWith('##') || /^[\d一二三四五六七八九十]+[、.：:]/.test(beautified)) {
      sections.push({ type: 'subtitle', content: beautified.replace(/^##\s*/, '') });
      i++;
      continue;
    }

    // # 標題
    if (beautified.startsWith('#') || beautified.startsWith('\u3010') || beautified.startsWith('\u25C6') || beautified.startsWith('\u25A0')) {
      sections.push({ type: 'title', content: beautified.replace(/^[#\u3010\u25C6\u25A0]+\s*/, '').replace(/\u3011$/, '') });
      i++;
      continue;
    }

    // 列表項
    if (/^[*\-\u2022\u00B7]\s+/.test(beautified) || /^[\d]+\.\s+/.test(beautified)) {
      sections.push({ type: 'list', content: beautified.replace(/^[*\-\u2022\u00B7\d.]+\s*/, '') });
      i++;
      continue;
    }

    // 高亮 (建議/注意/重要/提醒 開頭，避免誤觸包含這些字的描述文字)
    if (/^(建議|注意|重要|提醒|【建議|【注意|【重要|【提醒|★建議|★注意)/.test(beautified)) {
      sections.push({ type: 'highlight', content: beautified });
      i++;
      continue;
    }

    // 普通段落
    sections.push({ type: 'paragraph', content: beautified });
    i++;
  }

  return sections;
};

// ================================================================
// React 元件
// ================================================================

interface ReportContentProps {
  text: string;
}

const ReportContent: React.FC<ReportContentProps> = ({ text }) => {
  const sections = parseReportContent(text);

  return (
    <Box sx={{ lineHeight: 1.8 }}>
      {sections.map((section, index) => {
        switch (section.type) {
          case 'title':
            return (
              <Typography
                key={index}
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  color: '#4a148c',
                  mt: index > 0 ? 3 : 0,
                  mb: 1.5,
                  fontSize: '1.15rem',
                }}
              >
                {section.content}
              </Typography>
            );

          case 'subtitle':
            return (
              <Typography
                key={index}
                variant="subtitle1"
                sx={{
                  fontWeight: 'bold',
                  color: '#6a1b9a',
                  mt: 2,
                  mb: 1,
                  fontSize: '1rem',
                }}
              >
                {section.content}
              </Typography>
            );

          case 'red-title':
            return (
              <Typography
                key={index}
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  color: '#b71c1c',
                  mt: index > 0 ? 3 : 0,
                  mb: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                {section.content}
              </Typography>
            );

          case 'red-subtitle':
            return (
              <Typography
                key={index}
                variant="subtitle1"
                sx={{
                  fontWeight: 'bold',
                  color: '#c62828',
                  mt: 2,
                  mb: 1,
                  fontSize: '0.95rem',
                }}
              >
                {section.content}
              </Typography>
            );

          case 'highlight':
            return (
              <Alert
                key={index}
                severity="info"
                icon={<TipsAndUpdatesIcon />}
                sx={{
                  my: 1.5,
                  backgroundColor: '#e3f2fd',
                  '& .MuiAlert-message': { width: '100%' }
                }}
              >
                <Typography variant="body2">{section.content}</Typography>
              </Alert>
            );

          case 'list':
            return (
              <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', my: 0.5, pl: 2 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#667eea', mt: 1, mr: 1.5, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: '#333' }}>
                  {section.content}
                </Typography>
              </Box>
            );

          case 'divider':
            return (
              <Divider key={index} sx={{ my: 2, borderColor: '#764ba2', opacity: 0.3 }} />
            );

          case 'table':
            return (
              <TableContainer
                key={index}
                component={Paper}
                sx={{ my: 2, boxShadow: '0 2px 8px rgba(102, 126, 234, 0.1)', borderRadius: 2, overflow: 'hidden' }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      {section.tableData?.headers.map((header, hIndex) => (
                        <TableCell
                          key={hIndex}
                          align={section.tableData?.alignments?.[hIndex] || 'left'}
                          sx={{ fontWeight: 'bold', color: '#4a148c', borderBottom: '2px solid #764ba2' }}
                        >
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {section.tableData?.rows.map((row, rIndex) => (
                      <TableRow
                        key={rIndex}
                        sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' }, '&:hover': { backgroundColor: '#f0f0f0' } }}
                      >
                        {row.map((cell, cIndex) => (
                          <TableCell key={cIndex} align={section.tableData?.alignments?.[cIndex] || 'left'} sx={{ color: '#333' }}>
                            {cell}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            );

          case 'chart':
            return (
              <Box key={index} sx={{ my: 3 }}>
                {section.chartTitle && (
                  <Typography variant="subtitle2" sx={{ color: '#667eea', fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                    {section.chartTitle}
                  </Typography>
                )}
                <Box sx={{ backgroundColor: '#fafafa', borderRadius: 2, p: { xs: 1, sm: 2 }, border: '1px solid #e0e0e0' }}>
                  <ResponsiveContainer width="100%" height={Math.min(300, Math.max(200, (section.chartData?.length || 0) * 18))}>
                    <AreaChart data={section.chartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                      <defs>
                        <linearGradient id={`colorGrad${index}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#667eea" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#764ba2" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11, fill: '#666' }}
                        interval={section.chartData && section.chartData.length > 15 ? 2 : 0}
                        angle={section.chartData && section.chartData.length > 12 ? -45 : 0}
                        textAnchor={section.chartData && section.chartData.length > 12 ? 'end' : 'middle'}
                        height={section.chartData && section.chartData.length > 12 ? 50 : 30}
                      />
                      <YAxis tick={{ fontSize: 11, fill: '#666' }} domain={['auto', 'auto']} />
                      <Tooltip
                        contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #667eea', borderRadius: 8, fontSize: 13 }}
                        formatter={(value: any) => [Number(value).toFixed(1), '\u904B\u52E2\u503C']}
                      />
                      <ReferenceLine
                        y={section.chartData ? section.chartData.reduce((s, d) => s + d.value, 0) / section.chartData.length : 0}
                        stroke="#999"
                        strokeDasharray="5 5"
                        label={{ value: '\u5E73\u5747', position: 'right', fontSize: 10, fill: '#999' }}
                      />
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke="#667eea"
                        strokeWidth={2}
                        fill={`url(#colorGrad${index})`}
                        dot={{ r: 3, fill: '#667eea', stroke: '#fff', strokeWidth: 1 }}
                        activeDot={{ r: 5, fill: '#764ba2', stroke: '#fff', strokeWidth: 2 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            );

          case 'empty':
            return <Box key={index} sx={{ height: 8 }} />;

          case 'paragraph':
          default:
            return (
              <Typography key={index} variant="body2" sx={{ my: 0.5, color: '#333', textAlign: 'justify' }}>
                {section.content}
              </Typography>
            );
        }
      })}
    </Box>
  );
};

export default ReportContent;
